import React, { useState, useEffect, useRef } from 'react';
import { X, User, Mail, Building2, Check, Send } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { formatLocalDateTime } from '../../utils/dateUtils';

const AdminModal = ({ isOpen, mode, admin, tenants, onClose, onSave, onResendEmail }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tenantId: '',
    enabled: true,
    sendWelcomeEmail: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [adminCreated, setAdminCreated] = useState(false);
  const [createdAdminEmail, setCreatedAdminEmail] = useState('');

  // Use ref to track if we should preserve the success state
  const preserveSuccessState = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (admin && (mode === 'edit' || mode === 'view')) {
        setFormData({
          name: admin.name || '',
          email: admin.email || '',
          tenantId: admin.tenantId || '',
          enabled: admin.enabled !== false,
          sendWelcomeEmail: false // Don't send email for existing admins
        });
        setAdminCreated(false); // Reset success state for edit/view modes
        setCreatedAdminEmail(''); // Reset stored email
        preserveSuccessState.current = false;
      } else if (mode === 'add') {
        // Only reset form and success state when modal is first opened for add mode
        // Don't reset if we should preserve the success state
        if (!preserveSuccessState.current) {
          setFormData({
            name: '',
            email: '',
            tenantId: '',
            enabled: true,
            sendWelcomeEmail: true
          });
          setAdminCreated(false);
        }
      }
      setErrors({});
    } else {
      // Reset success state when modal is closed
      setAdminCreated(false);
      setCreatedAdminEmail('');
      preserveSuccessState.current = false;
    }
  }, [admin, mode, isOpen]);





  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const isSuperAdmin = admin && admin.roles && admin.roles.includes('ROLE_SUPER_ADMIN');

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    // Only require tenant for non-super admins
    if (!isSuperAdmin && !formData.tenantId) {
      newErrors.tenantId = 'Tenant selection is required';
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (mode === 'add') {
        // Create admin data without password (token-based system)
        const adminData = {
          ...formData,
          roles: ['ROLE_ADMIN'],
          isFirstTimeLogin: true,
          emailVerified: false
        };

        // Add tenant name to admin data for backend email sending
        if (formData.sendWelcomeEmail && formData.tenantId) {
          adminData.tenantName = tenants.find(t => t.id === formData.tenantId)?.name;
        }

        await superAdminAPI.createAdmin(adminData);

        // Store the email before resetting form for success message display
        setCreatedAdminEmail(formData.email);

        // Set a flag to show success message and preserve it
        setAdminCreated(true);
        preserveSuccessState.current = true;

        // Call onSave to refresh the admin list, but don't close the modal
        onSave && onSave(false); // false = don't close modal

        // Clear form errors on success
        setErrors({});

        // Reset form for potential next admin creation
        setFormData({
          name: '',
          email: '',
          tenantId: '',
          enabled: true,
          sendWelcomeEmail: true
        });

      } else if (mode === 'edit') {
        const updateData = {
          name: formData.name,
          email: formData.email,
          tenantId: formData.tenantId,
          enabled: formData.enabled
        };

        await superAdminAPI.updateAdmin(admin.id, updateData);

        // For edit mode, call onSave and close the modal
        onSave && onSave(true); // true = close modal
        onClose();
      }

    } catch (error) {
      console.error('Error saving admin:', error);
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Failed to save admin. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!admin || !admin.id) return;

    if (!window.confirm(`Resend welcome email to ${admin.name}?\n\nThis will generate a new temporary password and send it to ${admin.email}.`)) {
      return;
    }

    try {
      setLoading(true);
      const tenantName = admin.tenantId ? tenants.find(t => t.id === admin.tenantId)?.name : 'Your Institution';

      await superAdminAPI.resendAdminWelcomeEmail(admin.id, {
        tenantName: tenantName || 'Your Institution'
      });

      alert(`Welcome email resent successfully to ${admin.email}!\n\nA new temporary password has been generated and sent.`);

      if (onResendEmail) {
        onResendEmail(admin);
      }

      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error resending welcome email:', error);
      const errorMessage = error.response?.data?.message || 'Failed to resend welcome email. Please try again.';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isViewMode = mode === 'view';
  const title = mode === 'add' ? 'Add New Admin' : mode === 'edit' ? 'Edit Admin' : 'View Admin';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-responsive">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <User className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 p-4">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Admin Creation Success Display */}
          {mode === 'add' && adminCreated && (
            <div className="bg-green-50 border border-green-200 p-4 mb-6">
              <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                <Check className="h-4 w-4 mr-2" />
                Admin Created Successfully!
              </h4>
              <div className="space-y-3">
                <p className="text-sm text-green-700">
                  The admin account has been created successfully.
                </p>
                {formData.sendWelcomeEmail ? (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                    <h5 className="text-sm font-medium text-blue-800 mb-1 flex items-center">
                      <Send className="h-4 w-4 mr-2" />
                      Secure Setup Email Sent
                    </h5>
                    <p className="text-sm text-blue-700">
                      A secure account setup link has been sent to <strong>{createdAdminEmail}</strong>.
                    </p>
                    <ul className="text-xs text-blue-600 mt-2 space-y-1">
                      <li>• The admin will receive a professional welcome email</li>
                      <li>• Setup link expires in 24 hours for security</li>
                      <li>• No passwords are sent via email</li>
                      <li>• Admin will set their own secure password</li>
                    </ul>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                    <p className="text-sm text-yellow-700">
                      Account created but no setup email was sent. You can manually provide the admin with login instructions.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isViewMode}
                className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                placeholder="e.g., John Doe"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className={`input-field pl-10 ${errors.email ? 'border-red-300' : ''}`}
                  placeholder="admin@institution.edu"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Only show tenant selection for non-super admins */}
            {!(admin && admin.roles && admin.roles.includes('ROLE_SUPER_ADMIN')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    name="tenantId"
                    value={formData.tenantId}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`input-field pl-10 ${errors.tenantId ? 'border-red-300' : ''}`}
                  >
                    <option value="">Select a tenant</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.code})
                      </option>
                    ))}
                  </select>
                </div>
                {errors.tenantId && <p className="text-red-500 text-xs mt-1">{errors.tenantId}</p>}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Settings</h3>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Account enabled</span>
              </label>

              {mode === 'add' && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onChange={handleInputChange}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Send secure account setup email
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* View mode additional info */}
          {isViewMode && admin && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created At
                </label>
                <span className="text-sm text-gray-900">
                  {admin.createdAt ? formatLocalDateTime(admin.createdAt) : 'N/A'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Login
                </label>
                <span className="text-sm text-gray-900">
                  {admin.lastLoginAt ? formatLocalDateTime(admin.lastLoginAt) : 'Never'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Time Login
                </label>
                <span className={`text-sm ${admin.isFirstTimeLogin ? 'text-orange-600' : 'text-green-600'}`}>
                  {admin.isFirstTimeLogin ? 'Pending' : 'Completed'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Verified
                </label>
                <span className={`text-sm ${admin.emailVerified ? 'text-green-600' : 'text-red-600'}`}>
                  {admin.emailVerified ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-6 border-t">
            <div>
              {isViewMode && admin && (
                <button
                  type="button"
                  onClick={handleResendEmail}
                  disabled={loading}
                  className="btn-secondary flex items-center disabled:opacity-50"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Sending...' : 'Resend Welcome Email'}
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              {/* Show different buttons based on whether admin was created successfully */}
              {mode === 'add' && adminCreated ? (
                // Admin was created successfully - show Create Another and Close buttons
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setAdminCreated(false);
                      setCreatedAdminEmail('');
                      preserveSuccessState.current = false;
                    }}
                    className="btn-secondary"
                  >
                    Create Another
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </>
              ) : (
                // Normal state - show Cancel/Close and Create/Update buttons
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    {isViewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!isViewMode && (
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary disabled:opacity-50"
                    >
                      {loading ? (mode === 'add' ? 'Creating admin...' : 'Updating admin...') : mode === 'add' ? 'Create Admin' : 'Update Admin'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminModal;
